<template>
  <div class="timeline-dashboard-container">
    <section class="dashboard">
      <div>
        <CustomLoader v-if="isLoading" :width="70" />
      </div>

      <b-row class="mb-3">
        <b-col md="8">
          <h4 class="page-title mb-1">Assignment Timeline Dashboard</h4>
          <p class="text-muted mb-0">
            View and search assignment timeline logs.
          </p>
        </b-col>
      </b-row>

      <b-card class="border-0 shadow-sm">
        <b-row class="mb-3">
          <b-col md="3" class="mb-2">
            <b-form-input
              v-model="search"
              placeholder="Search logs by message..."
              @keyup.enter="fetchTimeline(1)"
            />
          </b-col>
          <b-col md="2" class="mb-2">
            <b-form-input
              v-model="filters.allModuleId"
              placeholder="Assignment No / AllModuleId"
              @keyup.enter="fetchTimeline(1)"
            />
          </b-col>
          <b-col md="2" class="mb-2">
            <b-form-select
              v-model="filters.shipmentType"
              :options="shipmentTypeOptions"
              @change="fetchTimeline(1)"
            />
          </b-col>
          <b-col md="2" class="mb-2">
            <b-form-select
              v-model="filters.dateType"
              :options="dateTypeOptions"
              @change="fetchTimeline(1)"
            />
          </b-col>
          <b-col md="3" class="mb-2">
            <date-range-picker
              v-model="filters.dateRange"
              :opens="'right'"
              :show-dropdowns="true"
              :auto-apply="true"
              @change="fetchTimeline(1)"
            />
          </b-col>
          <b-col md="12" class="text-right mb-2">
            <b-button variant="primary" @click="fetchTimeline(1)">
              <i class="fa fa-search mr-1" /> Search
            </b-button>
            <b-button variant="outline-secondary" class="ml-2" @click="clearFilters">
              Clear
            </b-button>
            <b-button variant="success" class="ml-2" @click="exportToExcel">
              <i class="fa fa-file-excel-o" /> Export
            </b-button>
          </b-col>
        </b-row>

        <b-table
          hover
          striped
          responsive
          show-empty
          :items="timelineData"
          :fields="fields"
          :busy="isBusy"
        >
          <template slot="cell(MessageDate)" slot-scope="data">
            {{ formatIST(data.item && data.item.MessageDate) }}
          </template>
          <template slot="cell(JobDate)" slot-scope="data">
            {{ formatIST(data.item && data.item.JobDate) }}
          </template>
          <template slot="table-busy">
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
            </div>
          </template>
        </b-table>

        <div
          class="d-flex justify-content-between align-items-center mt-2"
          v-if="totalRows > 0"
        >
          <div>
            Showing {{ (currentPage - 1) * perPage + 1 }} to
            {{ Math.min(currentPage * perPage, totalRows) }} of
            {{ totalRows }} entries
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @input="handlePageChange"
            class="mb-0"
          />
        </div>
      </b-card>
    </section>
  </div>
</template>

<script>
import LogManagementService from "../../services/LogManagementService";
import ExcelExportService from "../../services/ExcelExportService";
import CustomLoader from "../common/customLoader.vue";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import moment from "moment";

export default {
  name: "TimelineDashboard",
  beforeMount() {
      this.$store.dispatch("setCurrentRoute",this.$route.path);
  },
  components: { CustomLoader, DateRangePicker },
  data() {
    return {
      isLoading: false,
      isBusy: false,
      search: "",
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      filters: {
        allModuleId: "",
        shipmentType: "",
        dateType: "assbill_log_createdAt",
        dateRange: {
          startDate: "",
          endDate: "",
        },
      },
      shipmentTypeOptions: [
        { value: "", text: "All Shipment Types" },
        { value: "1", text: "Air" },
        { value: "2", text: "Sea" },
      ],
      dateTypeOptions: [
        { value: "assbill_log_createdAt", text: "Log Created Date" },
        { value: "loginTime", text: "Job Login Date" },
      ],
      fields: [
        { key: "JobNo", label: "Job No" },
        { key: "JobType", label: "Job Type" },
        { key: "JobDate", label: "Job Date" },
        { key: "ClientName", label: "Client" },
        { key: "ShippingLine", label: "Shipping Line" },
        { key: "ShipmentType", label: "Shipment Type" },
        { key: "PrealertStatus", label: "Prealert Status" },
        { key: "ManifestStatus", label: "Manifest Status" },
        { key: "MessageDate", label: "Log Date" },
        { key: "MessageCreatedBy", label: "Created By" },
        { key: "Message", label: "Log Message" },
      ],
      timelineData: [],
    };
  },
  created() {
    // default last 1 month
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    this.filters.dateRange.startDate = start.toISOString().slice(0, 10);
    this.filters.dateRange.endDate = end.toISOString().slice(0, 10);
    this.fetchTimeline(1);
  },
  methods: {
    formatIST(dateString) {
      if (!dateString) return "";
      return moment
        .utc(dateString)
        .utcOffset("+05:30")
        .format("YYYY-MM-DD HH:mm:ss");
    },
    buildFilters() {
      const Filters = {};
      if (this.filters.allModuleId) {
        Filters.allModuleId = this.filters.allModuleId;
      }
      if (this.filters.shipmentType) {
        Filters.shipmentType = this.filters.shipmentType;
      }
      if (
        this.filters.dateRange &&
        this.filters.dateRange.startDate &&
        this.filters.dateRange.endDate
      ) {
        Filters.dateRange = {
          startDate: this.filters.dateRange.startDate,
          endDate: this.filters.dateRange.endDate,
        };
      }
      if (this.filters.dateType) {
        Filters.dateType = this.filters.dateType;
      }
      return Filters;
    },
    fetchTimeline(page = 1) {
      this.isBusy = true;
      this.isLoading = true;
      this.currentPage = page || this.currentPage || 1;

      const payload = {
        pageNo: this.currentPage,
        pageSize: this.perPage,
        search_txt: this.search,
        isSelectedColumn: 0,
        Filters: this.buildFilters(),
      };

      LogManagementService.getTimelineLog(payload)
        .then((response) => {
          const records =
            response &&
            response.data &&
            response.data.data &&
            Array.isArray(response.data.data.data)
              ? response.data.data.data
              : [];
          this.timelineData = records;
          const pagination =
            response &&
            response.data &&
            response.data.data &&
            response.data.data.pagination
              ? response.data.data.pagination
              : {};
          this.totalRows = pagination.count || records.length || 0;
        })
        .catch(() => {
          this.timelineData = [];
          this.totalRows = 0;
        })
        .finally(() => {
          this.isBusy = false;
          this.isLoading = false;
        });
    },
    handlePageChange(page) {
      this.fetchTimeline(page);
    },
    clearFilters() {
      this.search = "";
      this.filters.allModuleId = "";
      this.filters.shipmentType = "";
       // reset to last 1 month
      const end = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - 1);
      this.filters.dateType = "assbill_log_createdAt";
      this.filters.dateRange.startDate = start.toISOString().slice(0, 10);
      this.filters.dateRange.endDate = end.toISOString().slice(0, 10);
      this.fetchTimeline(1);
    },
    exportToExcel() {
      const payload = {
        pageNo: 1,
        pageSize: 10,
        search_txt: this.search,
        isSelectedColumn: 0,
        generateExcel: 1,
        Filters: this.buildFilters(),
      };

      ExcelExportService.exportWithService(
        LogManagementService,
        "getTimelineLog",
        payload,
      )
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = "Timeline_Dashboard.xlsx";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        })
        .catch((error) => {
          console.error("Export error", error);
        });
    },
  },
};
</script>

<style scoped>
.timeline-dashboard-container {
  padding: 1rem;
}
</style>




// WEBPACK FOOTER //
// timelineDashboard.vue?e2cabd0e